import {
  type AccountSetupQuery,
  AdminV1ServiceTierTier,
  type FieldMask,
  type OnboardingSurveyFragment,
  type PartialOnboardingV1OnboardingSurveyInput,
  type UpdateOnboardingSurveyMutation,
  getError,
  getTypeOrNull,
  isNotFoundError,
  useAccountSetupQuery,
  useUpdateOnboardingSurveyMutation,
  withStandardUpdateHook,
} from '@spotify/confidence-graphql';

import type { SurveyAnswers } from '../../config/onboardingSurvey';

type OnboardingSurveyStatus = 'loading' | 'unanswered' | 'answered';

const ROLE_ONEOF_UPDATE_MASK: FieldMask<PartialOnboardingV1OnboardingSurveyInput> =
  ['role', 'customRole'];

const useUpdateOnboardingSurvey = withStandardUpdateHook<
  UpdateOnboardingSurveyMutation,
  OnboardingSurveyFragment,
  'updateOnboardingSurvey',
  PartialOnboardingV1OnboardingSurveyInput
>(useUpdateOnboardingSurveyMutation, 'updateOnboardingSurvey', {
  update: (cache, { data }) => {
    const survey = getTypeOrNull(data?.updateOnboardingSurvey);
    if (survey) {
      cache.modify({
        fields: { onboardingSurvey: () => survey },
      });
    }
  },
});

type OnboardingSurveyState = {
  status: OnboardingSurveyStatus;
  workspaceName?: string;
  isSaving: boolean;
  hasSaveError: boolean;
  saveAnswers: (answers: SurveyAnswers) => Promise<boolean>;
};

const toStatus = (
  loading: boolean,
  data: AccountSetupQuery | undefined,
): OnboardingSurveyStatus => {
  if (loading && !data) {
    return 'loading';
  }
  const isFreeTier =
    getTypeOrNull(data?.currentAccount)?.serviceTier?.tier ===
    AdminV1ServiceTierTier.Free;
  return isFreeTier && isNotFoundError(getError(data?.onboardingSurvey))
    ? 'unanswered'
    : 'answered';
};

const toWorkspaceName = (
  data: AccountSetupQuery | undefined,
): string | undefined => {
  const user = getTypeOrNull(data?.currentUser);
  return user?.accountMemberships.find(
    membership => membership.account === user.account,
  )?.displayName;
};

export const useOnboardingSurvey = (): OnboardingSurveyState => {
  const { data: currentData, previousData, loading } = useAccountSetupQuery();
  const data = currentData ?? previousData;
  const { updateOnboardingSurvey, isUpdating, error } =
    useUpdateOnboardingSurvey();

  const saveAnswers = (answers: SurveyAnswers): Promise<boolean> =>
    updateOnboardingSurvey(answers, ROLE_ONEOF_UPDATE_MASK).then(
      () => true,
      () => false,
    );

  return {
    status: toStatus(loading, data),
    workspaceName: toWorkspaceName(data),
    isSaving: isUpdating,
    hasSaveError: Boolean(error),
    saveAnswers,
  };
};
